/**
 * AI generation API endpoints (Phase 11)
 */

import { fetchApi } from '../core';
import type {
  AiArtifactType,
  AiGenerationListResponse,
  AiGenerationQueryParams,
  AiGenerationResponse,
  CreateAiGenerationRequest,
  DraftArtifactResult,
  PromptTemplateListResponse,
} from '../types';

function buildQuery(params: AiGenerationQueryParams = {}): string {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.artifact_type) search.set('artifact_type', params.artifact_type);
  if (typeof params.page === 'number') search.set('page', String(params.page));
  if (typeof params.limit === 'number') search.set('limit', String(params.limit));
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/** Generate (and deterministically validate) a draft artifact. Gated on the
 * backend behind the AI-generation feature entitlement. */
export function createGeneration(
  data: CreateAiGenerationRequest,
  componentName: string = 'Unknown'
) {
  return fetchApi<AiGenerationResponse>(
    '/ai-generations',
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function listGenerations(
  params: AiGenerationQueryParams = {},
  componentName: string = 'Unknown'
) {
  return fetchApi<AiGenerationListResponse>(
    `/ai-generations${buildQuery(params)}`,
    {},
    componentName
  );
}

export function getGeneration(id: string, componentName: string = 'Unknown') {
  return fetchApi<AiGenerationResponse>(`/ai-generations/${id}`, {}, componentName);
}

/** Turn a validated generation into a draft artifact. Quiz/open-response
 * generations yield a draft (unpublished) assignment; score-conversion
 * generations yield a score-conversion profile. The `kind` field tells the
 * caller which editor to hand off to. `approvedTagLabels` are the proposed
 * (new) skill tags the user approved — existing tags are linked regardless. */
export function createDraft(
  id: string,
  approvedTagLabels: string[] = [],
  componentName: string = 'Unknown'
) {
  return fetchApi<DraftArtifactResult>(
    `/ai-generations/${id}/draft`,
    { method: 'POST', body: JSON.stringify({ approved_tag_labels: approvedTagLabels }) },
    componentName
  );
}

/** Built-in starter prompt templates, optionally filtered to one artifact type. */
export function listPromptTemplates(
  artifactType?: AiArtifactType,
  componentName: string = 'Unknown'
) {
  const qs = artifactType ? `?artifact_type=${encodeURIComponent(artifactType)}` : '';
  return fetchApi<PromptTemplateListResponse>(
    `/ai-generations/templates${qs}`,
    {},
    componentName
  );
}
